import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  HomeIcon,
  BriefcaseIcon,
  UserGroupIcon,
  EnvelopeIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  UsersIcon,
  BoltIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  LinkIcon,
  SparklesIcon,
  ViewColumnsIcon,
  FunnelIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  BeakerIcon,
  DocumentPlusIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { FEATURES } from '../config/features';
import Logo from './Logo';

function Layout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { profile, logout } = useSupabaseAuth();
  const { unreadCount, notifications, markRead, markAllRead } = useNotifications();

  // Build user display data from profile
  const user = profile ? {
    firstName: profile.first_name,
    lastName: profile.last_name,
    name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
    email: profile.email,
    profilePic: profile.profile_pic || profile.avatar_url
  } : null;

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    window.location.href = '/login';
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    // Candidate side — the free AI job agent (acquisition + intent engine).
    { name: 'Auto Apply', href: '/apply', icon: RocketLaunchIcon },
    // Recruiter side — the intent-scored motivated-talent pool (the differentiator).
    { name: 'Pool Search', href: '/pool', icon: MagnifyingGlassIcon },
    { name: 'Role Matches', href: '/pool/roles', icon: SparklesIcon },
    { name: 'Talent Pool', href: '/app/talent-pool', icon: UsersIcon },
    { name: 'Pipeline', href: '/dashboard/pipeline', icon: ViewColumnsIcon },
    { name: 'Proof of Skill', href: '/proof', icon: ShieldCheckIcon },
    { name: 'Author Sample', href: '/proof/author', icon: DocumentPlusIcon },
    { name: 'ML Study', href: '/ml-study', icon: BeakerIcon },
    { name: 'Saved Searches', href: '/saved-searches', icon: FunnelIcon },
    { name: 'Job Recommendations', href: '/dashboard/recommendations', icon: SparklesIcon },
    { name: 'Jobs', href: '/jobs', icon: BriefcaseIcon },
    { name: 'Candidates', href: '/candidates', icon: UserGroupIcon },
    { name: 'Outreach', href: '/outreach', icon: EnvelopeIcon },
    // Autopilot is a deferred surface (see config/features.js) — hidden unless enabled.
    ...(FEATURES.autopilot ? [{ name: 'Autopilot', href: '/autopilot', icon: BoltIcon }] : []),
    { name: 'Collaboration', href: '/collaboration', icon: ShareIcon },
    { name: 'Feedback', href: '/feedback', icon: ChatBubbleLeftRightIcon },
    { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    { name: 'Job Matching', href: '/job-matching', icon: BriefcaseIcon },
    { name: 'Integrations', href: '/integrations', icon: LinkIcon },
    { name: 'Account Settings', href: '/account', icon: UserCircleIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <div className="relative z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center px-2">
                    <Link to="/"><Logo size={28} /></Link>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                className={`
                                  group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6
                                  ${location.pathname === item.href
                                    ? 'bg-gray-50 text-blue-600'
                                    : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                                  }
                                `}
                              >
                                <item.icon
                                  className={`h-6 w-6 shrink-0 ${location.pathname === item.href
                                    ? 'text-blue-600'
                                    : 'text-gray-400 group-hover:text-blue-600'
                                    }`}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center px-2">
            <Link to="/"><Logo size={28} /></Link>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`
                          group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6
                          ${location.pathname === item.href
                            ? 'bg-gray-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                          }
                        `}
                      >
                        <item.icon
                          className={`h-6 w-6 shrink-0 ${location.pathname === item.href
                            ? 'text-blue-600'
                            : 'text-gray-400 group-hover:text-blue-600'
                            }`}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Notification bell */}
              <Menu as="div" className="relative">
                <Menu.Button className="relative p-1.5 text-gray-400 hover:text-gray-500 focus:outline-none">
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2.5 w-80 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                      <span className="text-sm font-semibold text-gray-900">Notifications</span>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((n) => (
                        <Menu.Item key={n.id}>
                          {({ active }) => (
                            <button
                              onClick={() => !n.read && markRead(n.id)}
                              className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm ${
                                active ? 'bg-gray-50' : ''
                              } ${!n.read ? 'bg-blue-50/50' : ''}`}
                            >
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {n.title}
                                </p>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{n.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(n.created_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              {!n.read && (
                                <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      ))
                    )}
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* User menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="-m-1.5 flex items-center p-1.5">
                  <span className="sr-only">Open user menu</span>
                  <div className="flex items-center gap-x-4">
                    {user?.profilePic ? (
                      <img src={user.profilePic} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <UserCircleIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                    )}
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-semibold leading-6 text-gray-900">{user?.firstName || user?.name}</span>
                      <span className="text-xs text-gray-500">{user?.email}</span>
                    </div>
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/account"
                          className={`flex w-full items-center gap-x-2 px-3 py-2 text-sm leading-6 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'}`}
                        >
                          <UserCircleIcon className="h-4 w-4" aria-hidden="true" />
                          Account Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`flex w-full items-center gap-x-2 px-3 py-2 text-sm leading-6 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'}`}
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                          Logout
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout; 